import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { PaperPlaneTilt, Users, Trash, SpeakerSlash } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import MemberToken from '../components/commons/MemberToken';
import { Frame } from '../components/shell/AppShell';
import { isPrimary, moreFor } from '../lib/navigation';
import PageHeader from '../components/shell/PageHeader';
import commonsArt from '../assets/bg/commons.webp';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// No socket wiring yet, so the room refreshes on a timer. Fine for a society
// of this size; worth moving to the existing socket channel once the room is
// actually busy.
const POLL_MS = 10000;

/**
 * The Commons — the lounge's open room.
 *
 * A member is a number here and a name in their support thread, and the
 * dangerous confusion is not knowing which room you are speaking in
 * (docs/community_design.md §4.1). So this page is deliberately not a second
 * skin on the chat window: different header, different furniture, and the
 * member's own number kept on screen at all times.
 */
const Commons = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isStaff = user?.role === 'superadmin' || user?.role === 'operator';

  const [me, setMe] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const endRef = useRef(null);

  const load = useCallback(async (opts = {}) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/commons/messages`, {
        withCredentials: true,
      });
      // Anything but a list would take the whole page down on .map().
      setMessages(Array.isArray(data) ? data : []);
      setError('');
    } catch (e) {
      if (!opts.quiet) setError('Could not reach the Commons.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    axios.get(`${API_URL}/api/commons/me`, { withCredentials: true })
      .then(({ data }) => setMe(data))
      .catch(() => {});
    load();
  }, [load]);

  useEffect(() => {
    const t = setInterval(() => load({ quiet: true }), POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    const content = draft.trim();
    if (!content || sending) return;
    setSending(true);
    setError('');
    try {
      const { data } = await axios.post(
        `${API_URL}/api/commons/messages`, { content }, { withCredentials: true },
      );
      setMessages((prev) => [...prev, data]);
      setDraft('');
    } catch (err) {
      setError(err.response?.data?.detail || 'That did not go through.');
    } finally {
      setSending(false);
    }
  };

  const remove = async (id, wasMine) => {
    // Asked before taking down somebody ELSE'S post, because that is a power
    // and a slip of the thumb should not spend it. Not asked when withdrawing
    // your own: the usual reason is that something is on screen which should
    // not be, and "are you sure?" while it is still visible gets the order the
    // wrong way round.
    if (!wasMine && !window.confirm('Remove this post from the Commons?')) return;
    try {
      await axios.delete(`${API_URL}/api/commons/messages/${id}`, { withCredentials: true });
      // Matches what the server sends next: your own withdrawal leaves
      // nothing behind, somebody else's removal leaves a mark where it was.
      setMessages((prev) => (wasMine
        ? prev.filter((m) => m.id !== id)
        : prev.map((m) => (m.id === id ? { ...m, is_deleted: true, content: null } : m))));
    } catch (err) {
      setError('Could not remove that post.');
    }
  };

  const mute = async (userId) => {
    if (!window.confirm('Silence this member in the Commons? Their support thread and membership are untouched.')) return;
    try {
      await axios.post(
        `${API_URL}/api/commons/members/${userId}/mute`, {}, { withCredentials: true },
      );
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not mute that member.');
    }
  };

  const when = (iso) => {
    try {
      return formatDistanceToNow(new Date(iso), { addSuffix: true });
    } catch (e) {
      return '';
    }
  };

  const myNo = me?.member_no != null ? String(me.member_no).padStart(3, '0') : null;

  return (
    // A primary destination for members; for staff a child of More, which
    // stays lit and is where Back goes.
    <Frame user={user} background={commonsArt} activeKey={isPrimary(user, 'commons') ? 'commons' : 'more'} fill>
    <div className="flex-1 flex flex-col min-h-0" data-testid="commons-page">
      <PageHeader
        title="The Commons"
        subtitle="Members meet as numbers here — never by name"
        onBack={isPrimary(user, 'commons') ? undefined : () => navigate(moreFor(user))}
      >
        {/* Always on screen: a member must never be unsure which face they
            are wearing -- the same person is a name in Support. */}
        {me && (
          <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-border bg-card/70 px-3 py-1" data-testid="commons-you-are">
            <MemberToken memberNo={me.member_no} />
            <span className="text-xs text-foreground">You are No. {myNo || '—'} here</span>
          </div>
        )}
      </PageHeader>

      <div className="flex-1 min-h-0 overflow-y-auto px-3 py-3" data-testid="commons-messages">
        {loading ? (
          <p className="text-sm text-muted-foreground text-center py-10" role="status">Opening the room…</p>
        ) : messages.length === 0 ? (
          <div className="text-center py-16">
            <Users className="w-10 h-10 mx-auto mb-3 text-muted-foreground/40" aria-hidden="true" />
            <p className="text-sm text-muted-foreground">
              The room is quiet. You may be the first to speak.
            </p>
          </div>
        ) : (
          <div className="max-w-2xl mx-auto space-y-3">
            {messages.map((m) => {
              const mine = m.sender_id === user?.id;
              return (
                <div
                  key={m.id}
                  className={`flex items-start gap-2.5 ${mine ? 'flex-row-reverse' : ''}`}
                  data-testid={`commons-message-${m.id}`}
                >
                  <MemberToken memberNo={m.member_no} />
                  <div
                    className={[
                      'min-w-0 max-w-[82%] rounded-xl border px-3 py-2 backdrop-blur-sm bg-card/90',
                      mine ? 'border-primary/40' : 'border-border',
                    ].join(' ')}
                    // Your own words carry a gold tint over the same dense
                    // stone -- tinted, not see-through, so the art behind
                    // never shows as a smudge.
                    style={mine ? { backgroundImage: 'linear-gradient(hsl(var(--gold) / 0.10), hsl(var(--gold) / 0.10))' } : undefined}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className="text-[11px] font-semibold uppercase tracking-wider"
                        style={{ color: mine ? 'hsl(var(--gold))' : 'hsl(var(--muted-foreground))' }}
                      >
                        {mine ? `You · ${m.sender_label}` : m.sender_label}
                      </span>
                      {/* The author may withdraw their own; staff may take
                          down any. Shown only where it is permitted — a
                          control that refuses is worse than one that is
                          absent. Visible without hover: a phone has none. */}
                      {!m.is_deleted && (mine || isStaff) && (
                        <button
                          type="button"
                          onClick={() => remove(m.id, mine)}
                          className="ml-auto -my-2 -mr-2 flex h-10 w-10 items-center justify-center text-muted-foreground/70 hover:text-destructive"
                          aria-label="Remove this post"
                          data-testid={`commons-remove-${m.id}`}
                        >
                          <Trash className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {isStaff && !mine && (
                        <button
                          type="button"
                          onClick={() => mute(m.sender_id)}
                          className="-my-2 flex h-10 w-10 items-center justify-center text-muted-foreground/70 hover:text-destructive"
                          aria-label="Silence this member in the Commons"
                          data-testid={`commons-mute-${m.sender_id}`}
                        >
                          <SpeakerSlash className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    {/* anywhere, not break-word: only anywhere shrinks the
                        intrinsic width, which is what stops a long unbroken
                        run widening the row past the screen. */}
                    {m.is_deleted ? (
                      <p className="text-sm italic text-muted-foreground mt-0.5">
                        This post was removed
                      </p>
                    ) : (
                      <p
                        className="text-sm leading-relaxed whitespace-pre-wrap mt-0.5 text-foreground"
                        style={{ overflowWrap: 'anywhere' }}
                      >
                        {m.content}
                      </p>
                    )}
                    <p className={`mt-1 text-[10px] text-muted-foreground ${mine ? 'text-right' : ''}`}>
                      {when(m.created_at)}
                    </p>
                  </div>
                </div>
              );
            })}
            <div ref={endRef} />
          </div>
        )}
      </div>

      {me?.muted ? (
        <div className="border-t border-border bg-card/90 p-4 text-center" data-testid="commons-muted">
          <SpeakerSlash className="w-5 h-5 mx-auto mb-2 text-muted-foreground" aria-hidden="true" />
          <p className="text-sm text-muted-foreground">
            You cannot speak in the Commons at the moment. Your support thread is
            unaffected — write there if you need to reach the Society.
          </p>
        </div>
      ) : (
      <form onSubmit={send} className="border-t border-border bg-card/90 p-3" data-testid="commons-composer">
        <div className="max-w-2xl mx-auto">
          {error && <p role="alert" className="text-sm text-destructive mb-2" data-testid="commons-error">{error}</p>}
          <div className="flex items-end gap-2">
            {/* Text only: the Commons takes no attachments. The draft stays
                in the field if sending fails, so a retry is one tap. */}
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send(e);
                }
              }}
              placeholder="Write to The Commons…"
              aria-label="Write to The Commons"
              rows={1}
              maxLength={2000}
              className="min-h-[44px] max-h-40 resize-none"
              data-testid="commons-input"
            />
            <Button
              type="submit"
              disabled={sending || !draft.trim()}
              className="h-11 w-11 shrink-0 bg-primary hover:bg-primary/90"
              aria-label="Send"
              data-testid="commons-send"
            >
              <PaperPlaneTilt className="w-4 h-4" weight="fill" />
            </Button>
          </div>
        </div>
      </form>
      )}
    </div>
    </Frame>
  );
};

export default Commons;
